import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import { TFunction } from "i18next"
import { useRef } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { passwordValidationRule, requiredRule } from "@/utilities/form"

import styles from "./reset-password.module.css"

export const Route = createFileRoute("/auth/reset-password")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    email?: string
  } => {
    return {
      email: (search.email as string) || "",
    }
  },
  beforeLoad: ({ search }) => {
    // Redirect to sign-in if email is missing
    if (!search.email) {
      throw redirect({
        to: "/auth/sign-in",
        search: { qrLookupID: search.qrLookupID, redirect: search.redirect },
      })
    }
  },
  component: RouteComponent,
})

interface ResetPasswordFormSchema {
  digit1: string
  digit2: string
  digit3: string
  digit4: string
  digit5: string
  digit6: string
  newPassword: string
  confirmPassword: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function useResetPasswordMutation({
  form,
  email,
  qrLookupID,
  redirect,
  t,
}: {
  form: ReturnType<typeof useForm<ResetPasswordFormSchema>>
  email?: string
  qrLookupID?: string
  redirect?: string
  t: TFunction
}) {
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (data: ResetPasswordFormSchema) => {
      if (!email) {
        throw new Error(t("errors.auth.emailRequiredPasswordReset"))
      }

      const code = `${data.digit1}${data.digit2}${data.digit3}${data.digit4}${data.digit5}${data.digit6}`

      await api.auth.resetPassword({
        username: email,
        confirmationCode: code,
        newPassword: data.newPassword,
      })
      return api.auth.signIn({ username: email, password: data.newPassword })
    },
    onSuccess: () => {
      if (redirect) {
        navigate({ to: redirect })
      } else if (qrLookupID) {
        navigate({
          to: "/qr/$qrLookupID/welcome",
          params: { qrLookupID },
        })
      } else {
        navigate({
          to: "/",
        })
      }
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "reset-password",
        email,
        qrLookupID,
      })

      if (err.message === t("errors.auth.emailRequiredPasswordReset")) {
        form.setError("root", { message: err.message })
      } else {
        handleAuthError(err, form, t)
      }
    },
  })
}

function useResendResetCodeMutation({ email }: { email?: string }) {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async () => {
      if (!email) {
        throw new Error(t("errors.auth.emailRequiredResend"))
      }

      return api.auth.forgotPassword({
        username: email,
      })
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "resend-reset-code",
        email,
      })
    },
  })
}

function RouteComponent() {
  const { email, qrLookupID, redirect } = Route.useSearch()
  const { t } = useTranslation()

  const form = useForm<ResetPasswordFormSchema>({
    defaultValues: {
      digit1: "",
      digit2: "",
      digit3: "",
      digit4: "",
      digit5: "",
      digit6: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onChange",
  })

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const resetPasswordMutation = useResetPasswordMutation({
    form,
    email,
    qrLookupID,
    redirect,
    t,
  })
  const resendMutation = useResendResetCodeMutation({ email })

  const onSubmit = (data: ResetPasswordFormSchema) => {
    form.clearErrors("root")
    resetPasswordMutation.mutate(data)
  }

  const handleResendCode = () => {
    form.clearErrors("root")
    resendMutation.mutate()
  }

  const digitFields = [
    "digit1",
    "digit2",
    "digit3",
    "digit4",
    "digit5",
    "digit6",
  ] as const

  const handlePaste = (pastedText: string) => {
    const digits = pastedText.replace(/\D/g, "").slice(0, 6)
    if (digits.length === 0) return
    form.clearErrors("root")

    // Clear all fields first
    digitFields.forEach((fieldName) => {
      form.setValue(fieldName, "", { shouldValidate: false })
    })

    // Fill fields with available digits
    for (let i = 0; i < digits.length && i < digitFields.length; i++) {
      form.setValue(digitFields[i], digits[i], {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

    // Focus the next empty field, or the last field if all are filled
    const focusIndex = Math.min(digits.length, digitFields.length - 1)
    inputRefs.current[focusIndex]?.focus()
  }

  return (
    <>
      <Heading level={1} as={1}>
        {t("auth.resetPassword.heading")}
      </Heading>

      <Text level={2} className={styles.descriptionText}>
        {t("auth.resetPassword.description")}
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset
          className={styles.fieldset}
          disabled={resetPasswordMutation.isPending}
        >
          <Label>{t("auth.resetPassword.verificationCode")}</Label>

          {/* 6-Digit Code Input */}
          <div className={styles.codeInputRow}>
            {digitFields.map((fieldName, index) => (
              <Controller<ResetPasswordFormSchema>
                key={fieldName}
                name={fieldName}
                control={form.control}
                rules={{
                  required: requiredRule(t("errors.common.required")),
                  pattern: {
                    value: /^[0-9]$/,
                    message: t("errors.common.singleDigit"),
                  },
                }}
                render={({ field: { ref, ...rest } }) => {
                  return (
                    <TextField
                      aria-label={`Verification code digit ${index + 1}`}
                    >
                      <TextField.Input
                        {...rest}
                        innerRef={(el) => {
                          inputRefs.current[index] = el
                          if (typeof ref === "function") ref(el)
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className={styles.codeDigit}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "")

                          // Clear errors when user starts typing
                          if (form.formState.errors.root)
                            form.clearErrors("root")

                          if (val.length > 1) {
                            // Only handle paste if we're not at the last input or if current input is empty
                            if (index < digitFields.length - 1 || !rest.value) {
                              handlePaste(val)
                            } else {
                              // At the last input with existing value - just take the first digit
                              rest.onChange(val.charAt(0))
                            }
                          } else if (val.length === 1) {
                            rest.onChange(val)
                            if (index < digitFields.length - 1) {
                              inputRefs.current[index + 1]?.focus()
                            }
                          } else if (val.length === 0) {
                            rest.onChange("")
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault()
                          const pastedText = e.clipboardData.getData("text")
                          handlePaste(pastedText)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace") {
                            if (rest.value === "") {
                              if (index > 0) {
                                inputRefs.current[index - 1]?.focus()
                              }
                            } else {
                              rest.onChange("")
                            }
                          } else if (e.key === "ArrowLeft" && index > 0) {
                            inputRefs.current[index - 1]?.focus()
                          } else if (
                            e.key === "ArrowRight" &&
                            index < digitFields.length - 1
                          ) {
                            inputRefs.current[index + 1]?.focus()
                          }
                        }}
                      />
                    </TextField>
                  )
                }}
              />
            ))}
          </div>
          <Controller<ResetPasswordFormSchema>
            name="newPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("errors.common.required")),
              ...passwordValidationRule(t),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.resetPassword.newPassword")}</Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<ResetPasswordFormSchema>
            name="confirmPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("errors.common.required")),
              validate: (value) => {
                const newPassword = form.getValues("newPassword")
                return (
                  value === newPassword || t("errors.common.passwordMismatch")
                )
              },
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.resetPassword.confirmPassword")}</Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            hue="secondary"
            variant="default"
            type="submit"
            isPending={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending
              ? t("auth.resetPassword.submittingButton")
              : t("auth.resetPassword.submitButton")}
          </Button>
        </fieldset>
      </form>

      {/* Resend Code Link */}
      <Text level={2} className={styles.resendContainer}>
        {t("auth.resetPassword.didntReceiveCode")}{" "}
        <Button
          hue="primary"
          presentation="link"
          onPress={handleResendCode}
          isDisabled={
            resendMutation.isPending || resetPasswordMutation.isPending
          }
          style={{
            textDecorationLine: "underline",
            opacity:
              resendMutation.isPending || resetPasswordMutation.isPending
                ? 0.5
                : 1,
          }}
        >
          {resendMutation.isPending
            ? t("auth.resetPassword.resendingCode")
            : t("auth.resetPassword.resendCode")}
        </Button>
        .
      </Text>
    </>
  )
}
